"use client";

// Built on Base UI's unstyled Accordion — see full API at:
// https://base-ui.com/react/components/accordion

import * as React from "react";
import { cn } from "@marketing-sites/utils/cn";
import { Accordion as BaseAccordion } from "@base-ui/react/accordion";
import { getAnalyticsRegion } from "@marketing-sites/analytics/get-analytics-region";
import { triggerAnalyticsClick } from "@marketing-sites/analytics/trigger-analytics-event";
import { TrackingType } from "@marketing-sites/analytics/types";

export function Trigger({
  className,
  onClick,
  analyticsId,
  ...rest
}: React.ComponentProps<typeof BaseAccordion.Trigger> & {
  analyticsId: string;
}) {
  const handleClick: typeof onClick = (event) => {
    const trigger = event.currentTarget;
    const isOpen = trigger.getAttribute("aria-expanded") === "true";

    if (!isOpen) {
      const location = getAnalyticsRegion(trigger) ?? "Unknown";
      const raw =
        trigger.textContent?.replace(/\s+/g, " ").trim() ?? "Accordion Item";
      const text = raw.length > 25 ? `${raw.slice(0, 25).trim()}…` : raw;

      triggerAnalyticsClick({
        text,
        location,
        type: TrackingType.Button,
        analyticsId,
      });
    }

    onClick?.(event);
  };

  return (
    <BaseAccordion.Trigger
      className={cn(
        "focus-visible:z-1 group relative flex w-full items-baseline justify-between text-left focus-visible:outline-2 focus-visible:outline-blue-800",
        className,
      )}
      onClick={handleClick}
      {...rest}
    />
  );
}

export function Panel({
  className,
  ...rest
}: React.ComponentProps<typeof BaseAccordion.Panel>) {
  return (
    <BaseAccordion.Panel
      className={cn(
        "h-(--accordion-panel-height) data-endingStyle:h-0 data-startingStyle:h-0 overflow-hidden",
        className,
      )}
      {...rest}
    />
  );
}

export type RootProps = BaseAccordion.Root.Props;

export function Root({ onValueChange, children, ...rest }: RootProps) {
  return (
    <BaseAccordion.Root onValueChange={onValueChange} {...rest}>
      {children}
    </BaseAccordion.Root>
  );
}

export const Item = BaseAccordion.Item;
export const Header = BaseAccordion.Header;
